import React, { useState, useEffect, useRef } from 'react';
import {
  Link,
  useHistory,
  useLocation
} from "react-router-dom";
import { useAuth } from '../../context/AuthContext';
import { useFilterContext } from '../../context/SharedFilterContext';
import {Dropdown} from 'react-bootstrap';
import { FcInspection, FcDocument, FcFactoryBreakdown, FcElectricalSensor} from 'react-icons/fc';
import {FaUserCircle} from 'react-icons/fa';
import DatePicker from 'react-date-picker';
import moment from 'moment';
import User from '../../models/User';
import PropertyList from '../../models/PropertyList';
import NotificationsBell from './NotificationsBell';

export const TopNavBar = () => {
    return (
        <nav className="navbar navbar-dark d-flex justify-content-between bg-light flex-lg-nowrap p-0 m-0 mb-1 " style={{boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.15)", overflow: 'visible', zIndex: 1000}}>
            <div style={{width: '250px'}}>
              <Logos/>
            </div>
            <div className="d-flex flex-column col-6 ml-3 mr-3">
              <TopNavIconList iconSize={36}/>
            </div>
            <div style={{width: '140px'}}>
              <NavigationLinkList />
            </div>

        </nav>
    );
};


const Logos = () => {
    const {user} = useAuth();
  
    return (
      <div className="d-flex flex-row justify-content-left">
        {(user && user.company && user.company.image) ? 
          <Link to="/">
            <img
              className="ml-2 mb-1 align-self-end image-responsive"
              style={{ maxHeight: "90px", width: "auto"}}
              src={`https://s3.amazonaws.com/${user.company.image.bucket}/${user.company.image.key}?cachebust=${(new Date().valueOf())}`}
              alt="Company Logo"
            />
          </Link>
          :
          <Link className="navbar-brand col-xs-1 mr-0 ml-1" style={{ height: "120px" }} to="/start">
            <img
              className="mb-1"
              style={{ height: "110px"}}
              src="/DST_animated_logo_once1.gif"
              alt="DST Logo"
            />
          </Link>
        }
      </div>
    )
}
  
const DATE_PRESETS = [
    { key: 'last30', label: 'Last 30 Days' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'thisQuarter', label: 'This Quarter' },
    { key: 'thisYear', label: 'This Year' },
    { key: 'lastWeek', label: 'Last Week' },
    { key: 'lastMonth', label: 'Last Month' },
    { key: 'lastQuarter', label: 'Last Quarter' },
    { key: 'lastYear', label: 'Last Year' },
    { key: 'custom', label: 'Custom' },
];

const getPresetDates = (key) => {
    const today = moment().endOf('day').format('YYYY-MM-DD');
    switch (key) {
        case 'last30':
            return { start: moment().subtract(30, 'days').format('YYYY-MM-DD'), end: today };
        case 'thisWeek':
            return { start: moment().startOf('week').format('YYYY-MM-DD'), end: today };
        case 'thisMonth':
            return { start: moment().startOf('month').format('YYYY-MM-DD'), end: today };
        case 'thisQuarter':
            return { start: moment().startOf('quarter').format('YYYY-MM-DD'), end: today };
        case 'thisYear':
            return { start: moment().startOf('year').format('YYYY-MM-DD'), end: today };
        case 'lastWeek':
            return { start: moment().subtract(1, 'week').startOf('week').format('YYYY-MM-DD'), end: moment().subtract(1, 'week').endOf('week').format('YYYY-MM-DD') };
        case 'lastMonth':
            return { start: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), end: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD') };
        case 'lastQuarter':
            return { start: moment().subtract(1, 'quarter').startOf('quarter').format('YYYY-MM-DD'), end: moment().subtract(1, 'quarter').endOf('quarter').format('YYYY-MM-DD') };
        case 'lastYear':
            return { start: moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'), end: moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD') };
        default:
            return null;
    }
};

const TopNavIconList = ({iconSize}) => {
    const {user} = useAuth();
    const location = useLocation();
    const {
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        inspectionViewMode,
        setInspectionViewMode,
        selectedPropertyList,
        setSelectedPropertyList,
        favoritedPropertyIds,
        setFavoritedPropertyIds,
        propertyListIds,
        setPropertyListIds,
        availablePropertyLists,
        setAvailablePropertyLists,
        hasFavorites,
        setHasFavorites,
        hasPropertyLists,
        setHasPropertyLists
    } = useFilterContext();

    const [tempStartDate, setTempStartDate] = useState(startDate);
    const [tempEndDate, setTempEndDate] = useState(endDate);
    const [userDataFetched, setUserDataFetched] = useState(false);

    // Determine initial preset: if URL has custom dates, start in custom mode
    const params = new URLSearchParams(window.location.search);
    const hasUrlDates = params.get('startDate') || params.get('endDate');
    const [selectedPreset, setSelectedPreset] = useState(hasUrlDates ? 'custom' : 'last30');

    // Auto-detect preset when dates change programmatically (e.g., from search input)
    // Skip initial mount to respect the default preset
    const isInitialMountRef = useRef(true);
    useEffect(() => {
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            return;
        }
        for (const preset of DATE_PRESETS) {
            if (preset.key === 'custom') continue;
            const dates = getPresetDates(preset.key);
            if (dates && dates.start === startDate && dates.end === endDate) {
                setSelectedPreset(preset.key);
                return;
            }
        }
        // No preset matched — show custom with the actual dates
        setSelectedPreset('custom');
        setTempStartDate(startDate);
        setTempEndDate(endDate);
    }, [startDate, endDate]);

    // Check which pages should show filters
    const isInspectionsPage = location.pathname.startsWith('/inspections');
    const isPropertiesPage = location.pathname.startsWith('/properties');
    const isPropertyEditPage = location.pathname.startsWith('/property/edit');
    const isReportsPage = location.pathname.startsWith('/reports');
    const isWorkOrdersScreen = location.pathname.startsWith('/work_orders_screen');
    const isCompanyActivityPage = location.pathname === '/company_activity_report' ||
                                  location.pathname === '/company_activity_report/' ||
                                  location.pathname === '/company_admin_dashboard' ||
                                  location.pathname === '/company_admin_dashboard/' ||
                                  location.pathname === '/' ||
                                  location.pathname === '/start';

    // Show date range on all pages except Property (Edit) and Property Edit form
    const showDateRange = !isPropertyEditPage && !isPropertiesPage && (isInspectionsPage || isReportsPage || isCompanyActivityPage || isWorkOrdersScreen);

    // Show view mode toggles on all specified pages
    const showViewToggles = isInspectionsPage || isPropertiesPage || isPropertyEditPage || isReportsPage || isCompanyActivityPage || isWorkOrdersScreen;

    // Fetch user data and property lists on mount
    useEffect(() => {
        if (!user || userDataFetched) return;

        const fetchUserData = async () => {
            try {
                // Fetch user's favorite properties
                const currentUserData = await User.findByUUID(user.uuid);
                if (currentUserData?.favorite_properties) {
                    const favorites = JSON.parse(currentUserData.favorite_properties);
                    setHasFavorites(favorites && favorites.length > 0);
                    setFavoritedPropertyIds(favorites || []);
                } else {
                    setHasFavorites(false);
                    setFavoritedPropertyIds([]);
                }

                // Fetch available property lists for the company
                if (user.company?.id) {
                    const lists = await PropertyList.getPropertyListsByCompany(user.company.id);
                    setAvailablePropertyLists(lists || []);
                    setHasPropertyLists(lists && lists.length > 0);
                }

                setUserDataFetched(true);

                // Set default view mode if not set
                if (!inspectionViewMode) {
                    setInspectionViewMode('inspection');
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
                setUserDataFetched(true);
            }
        };

        fetchUserData();
    }, [user, userDataFetched]);

    if (!user){
        return <div/>;
    }

    if (!showDateRange && !showViewToggles) {
        return <div/>;
    }

    const applyDateRange = () => {
        setStartDate(tempStartDate);
        setEndDate(tempEndDate);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            applyDateRange();
        }
    };

    const handleViewModeToggle = (mode) => {
        if (mode === 'favorited' && !hasFavorites) return;
        if (mode === 'propertyList' && !selectedPropertyList) return;

        setInspectionViewMode(mode);

        // Update property list IDs when switching to property list mode
        if (mode === 'propertyList' && selectedPropertyList) {
            const list = availablePropertyLists.find(l => l.uuid === selectedPropertyList.uuid);
            if (list?.properties_list) {
                const ids = JSON.parse(list.properties_list);
                setPropertyListIds(ids || []);
            }
        } else if (mode !== 'propertyList') {
            setPropertyListIds([]);
            setSelectedPropertyList(null);  // Clear the dropdown selection when switching away from property list mode
        }
    };

    const handlePropertyListChange = (listUuid) => {
        const list = availablePropertyLists.find(l => l.uuid === listUuid);
        if (list) {
            setSelectedPropertyList(list);
            if (list.properties_list) {
                const ids = JSON.parse(list.properties_list);
                setPropertyListIds(ids || []);
            }
            // Auto-switch to property list mode when a list is selected
            if (inspectionViewMode !== 'propertyList') {
                setInspectionViewMode('propertyList');
            }
        }
    };

    return (
        <div className="d-flex flex-row justify-content-center align-items-center" style={{gap: '15px'}}>
            {/* Date Range Selector - Not shown on Property Edit page */}
            {showDateRange && (
                <>
                    <select
                        value={selectedPreset}
                        onChange={(e) => {
                            const key = e.target.value;
                            setSelectedPreset(key);
                            if (key !== 'custom') {
                                const dates = getPresetDates(key);
                                if (dates) {
                                    setStartDate(dates.start);
                                    setEndDate(dates.end);
                                }
                            }
                        }}
                        style={{
                            fontSize: '13px',
                            fontWeight: '500',
                            padding: '6px 12px',
                            borderRadius: '4px',
                            border: '1px solid #ced4da',
                            backgroundColor: '#fff',
                            color: '#333',
                            cursor: 'pointer',
                            outline: 'none',
                            minWidth: '160px'
                        }}
                    >
                        {DATE_PRESETS.map((preset, idx) => (
                            <option key={preset.key} value={preset.key}
                                style={preset.key === 'custom' ? {borderTop: '1px solid #ccc'} : {}}
                            >
                                {preset.key === 'custom' ? '── Custom ──' : preset.label}
                            </option>
                        ))}
                    </select>
                    {selectedPreset === 'custom' && (
                        <>
                            <DatePicker
                                onChange={(value) => {
                                    const newStartDate = moment(value).endOf("day").format("YYYY-MM-DD");
                                    setTempStartDate(newStartDate);
                                }}
                                value={moment(tempStartDate).toDate()}
                                className="ml-1"
                                calendarIcon={null}
                                clearIcon={null}
                                onKeyPress={handleKeyPress}
                            />
                            <span style={{fontSize: '14px'}}>to</span>
                            <DatePicker
                                onChange={(value) => {
                                    const newEndDate = moment(value).endOf("day").format("YYYY-MM-DD");
                                    setTempEndDate(newEndDate);
                                }}
                                value={moment(tempEndDate).toDate()}
                                className="ml-1"
                                calendarIcon={null}
                                clearIcon={null}
                                onKeyPress={handleKeyPress}
                            />
                            <button
                                onClick={applyDateRange}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#0056b3',
                                    color: '#fff',
                                    border: '1px solid #0056b3',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    outline: 'none'
                                }}
                            >
                                Apply
                            </button>
                        </>
                    )}
                </>
            )}

            {/* View Mode Toggles */}
            {showViewToggles && (
                <div className="btn-group" role="group" style={{marginLeft: showDateRange ? '20px' : '0'}}>
                    <button
                        type="button"
                        className={`btn ${inspectionViewMode === 'favorited' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleViewModeToggle('favorited')}
                        disabled={!hasFavorites}
                        style={{fontSize: '13px', padding: '6px 12px'}}
                    >
                        Favorites
                    </button>
                    <Dropdown as="span">
                        <Dropdown.Toggle
                            variant={inspectionViewMode === 'propertyList' ? 'primary' : 'outline-primary'}
                            disabled={!hasPropertyLists}
                            style={{fontSize: '13px', padding: '6px 12px', borderRadius: '0', borderLeft: 'none', borderRight: 'none'}}
                        >
                            {selectedPropertyList && inspectionViewMode === 'propertyList' ? selectedPropertyList.name : 'Property Groups'}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {availablePropertyLists.map(list => (
                                <Dropdown.Item
                                    key={list.uuid}
                                    active={selectedPropertyList?.uuid === list.uuid}
                                    onClick={() => handlePropertyListChange(list.uuid)}
                                >
                                    {list.name}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    <button
                        type="button"
                        className={`btn ${inspectionViewMode === 'inspection' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => handleViewModeToggle('inspection')}
                        style={{fontSize: '13px', padding: '6px 12px'}}
                    >
                        All View
                    </button>
                </div>
            )}
        </div>
    )
}

const NavigationLinkList = () => {
    const {user, signout, isAdminOrHigher} = useAuth();
    const history = useHistory();
    const [selectedCompanyGroup, setSelectedCompanyGroup] = React.useState({});
    const [showCompanyGroups, setShowCompanyGroups] = React.useState(false);

    const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
      <a
        href=""
        ref={ref}
        onClick={(e) => {
          e.preventDefault();
          onClick(e);
        }}
        style={{color: 'black'}}
      >
        {children}
      </a>
    ));

    if (!user && history.location.pathname.indexOf('auth') >= 0){
      return <div/>;
    }

    const companyGroups = user ? User.allCompanyGroups(user) : [];

    return (
      <div
        className="d-flex flex-row align-items-center justify-content-end"
        style={{ gap: '18px', paddingRight: '18px' }}
      >
        {user && <NotificationsBell/>}
        {!user ? <></>
          :
          <Dropdown align="end" onToggle={(isOpen) => { if (!isOpen) setShowCompanyGroups(false); }}>
              <Dropdown.Toggle as={CustomToggle} id="user-dropdown">
                <FaUserCircle style={{fontSize: 36}}/>
              </Dropdown.Toggle>

              <Dropdown.Menu style={{ minWidth: '240px' }} onClick={(e) => e.stopPropagation()}>
                <div style={{ padding: '4px 16px', fontSize: '11px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Company Group</div>
                <div
                  style={{ padding: '4px 16px', fontSize: '14px', color: '#333', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onClick={() => setShowCompanyGroups(!showCompanyGroups)}
                >
                  <span>{selectedCompanyGroup.title || 'Company Group'}</span>
                  <span style={{ fontSize: '10px', color: '#888' }}>{showCompanyGroups ? '▲' : '▼'}</span>
                </div>
                {showCompanyGroups && (
                  <div style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', margin: '4px 8px', padding: '4px 0' }}>
                    {companyGroups.map(cg => (
                      <div
                        key={cg?.title}
                        style={{
                          padding: '6px 16px',
                          fontSize: '13px',
                          cursor: isAdminOrHigher() ? 'pointer' : 'default',
                          backgroundColor: selectedCompanyGroup.title === cg?.title ? '#e2e6ea' : 'transparent'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e2e6ea'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = selectedCompanyGroup.title === cg?.title ? '#e2e6ea' : 'transparent'; }}
                        onClick={() => {
                          if (isAdminOrHigher()) {
                            setSelectedCompanyGroup(cg);
                            setShowCompanyGroups(false);
                          }
                        }}
                      >{cg?.title}</div>
                    ))}
                  </div>
                )}
                <Dropdown.Divider />
                <div style={{ padding: '4px 16px', fontSize: '11px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>Company</div>
                <div style={{ padding: '4px 16px', fontSize: '14px', color: '#333' }}>
                  {user.company?.title || ''}
                </div>
                <Dropdown.Divider />
                <div style={{ padding: '4px 16px', fontSize: '11px', color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>User</div>
                <div style={{ padding: '4px 16px', fontSize: '14px', color: '#333' }}>
                  {user.full_name || ''}
                </div>
                <Dropdown.Divider />
                <Dropdown.Item onClick={(e) => e.stopPropagation()}>
                  <Link className="nav-link text-dark" style={{padding: 0, paddingLeft: 10}} to={`/user/edit/${user.uuid}`}>Edit Settings</Link>
                </Dropdown.Item>
                <Dropdown.Item onClick={(e) => e.stopPropagation()}>
                  <Link className="nav-link text-dark" style={{padding: 0, paddingLeft: 10}} to="/auth" onClick={() => {
                    signout();
                    history.push("/auth")
                  }}>Sign Out</Link>
                </Dropdown.Item>
              </Dropdown.Menu>
          </Dropdown>
        }
      </div>
    )
  }